import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ExternalLink, BookOpen, Video, Wrench, Globe, FileText, Clock } from 'lucide-react';

const getIcon = (type, isLarge, image, title) => {
  const imageSizeClass = isLarge ? "w-12 h-12" : "w-10 h-10";
  
  if (image) {
    return (
      <img 
        src={image} 
        alt={title} 
        className={`${imageSizeClass} rounded-lg object-cover grayscale group-hover:grayscale-0 transition-all duration-500`} 
      />
    );
  }

  const iconSizeClass = isLarge ? "w-6 h-6" : "w-5 h-5";
  const colorClass = "text-gray-900 dark:text-white";
  
  switch (type?.toLowerCase()) {
    case 'book':
      return <BookOpen className={`${iconSizeClass} ${colorClass}`} />;
    case 'youtube channel':
    case 'course':
      return <Video className={`${iconSizeClass} ${colorClass}`} />;
    case 'tool':
      return <Wrench className={`${iconSizeClass} ${colorClass}`} />;
    case 'website':
      return <Globe className={`${iconSizeClass} ${colorClass}`} />;
    case 'bonus':
      return <FileText className={`${iconSizeClass} ${colorClass}`} />;
    case 'coming soon':
      return <Clock className={`${iconSizeClass} ${colorClass}`} />;
    default:
      return <ExternalLink className={`${iconSizeClass} ${colorClass}`} />;
  }
};

const getButtonText = (type) => {
  switch (type?.toLowerCase()) {
    case 'book':
      return 'READ_BOOK';
    case 'youtube channel':
    case 'course':
      return 'WATCH_CONTENT';
    case 'tool':
      return 'LAUNCH_TOOL';
    case 'website':
      return 'ACCESS_SITE';
    case 'bonus':
      return 'DOWNLOAD_FILE';
    case 'coming soon':
      return 'IN_DEVELOPMENT';
    default:
      return 'OPEN_LINK';
  }
};

const Card = ({ title, type, description, link, image, variant = 'small', spanClass = '', index = 0 }) => {
  const staggerClass = `stagger-${(index % 5) + 1}`;
  const isLarge = variant === 'hero' || variant === 'tall' || variant === 'wide';
  const [isHovered, setIsHovered] = useState(false);
  const [needsClamp, setNeedsClamp] = useState(false);
  const [canHover, setCanHover] = useState(false);
  const [fullHeightPx, setFullHeightPx] = useState(0);
  const descRef = useRef(null);

  const sizeClass =
    variant === 'hero'
      ? 'min-h-[360px]'
      : variant === 'tall'
        ? 'min-h-[380px]'
        : variant === 'wide'
          ? 'min-h-[320px]'
          : 'min-h-[260px]';

  const collapsedMaxHeightPx =
    variant === 'hero'
      ? 160
      : variant === 'tall'
        ? 140
        : variant === 'wide'
          ? 120
          : 92;

  const recomputeClamp = useCallback(() => {
    const el = descRef.current;
    if (!el) return;
    const h = el.scrollHeight || 0;
    setFullHeightPx(h);
    setNeedsClamp(h > collapsedMaxHeightPx + 1);
  }, [collapsedMaxHeightPx]);

  useEffect(() => {
    const mq = window.matchMedia?.('(hover: hover) and (pointer: fine)');
    if (!mq) return;

    const update = () => setCanHover(Boolean(mq.matches));
    update();

    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  useLayoutEffect(() => {
    recomputeClamp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [description, variant, recomputeClamp]);

  useEffect(() => {
    const onResize = () => recomputeClamp();
    window.addEventListener('resize', onResize);

    // Recompute after fonts load (helps with accurate measurements).
    const ready = document.fonts?.ready;
    if (ready?.then) ready.then(() => recomputeClamp()).catch(() => {});

    return () => window.removeEventListener('resize', onResize);
  }, [recomputeClamp]);

  const shouldClamp = canHover && needsClamp;
  const expandedMaxHeightPx = Math.max(fullHeightPx + 8, collapsedMaxHeightPx);
  const maxHeight = shouldClamp
    ? (isHovered ? `${expandedMaxHeightPx}px` : `${collapsedMaxHeightPx}px`)
    : undefined;

  return (
    <a 
      href={link} 
      target={link === '#' ? '_self' : '_blank'} 
      rel="noopener noreferrer"
      onClick={(e) => { if (link === '#') e.preventDefault(); }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
      className={`group relative overflow-hidden flex flex-col p-6 md:p-8 rounded-2xl shadow-sm hover:shadow-2xl transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] hover:-translate-y-1 hover:z-10 border border-gray-200 dark:border-gray-800 opacity-0 animate-fade-in-up ${staggerClass} ${sizeClass} ${spanClass} bg-white dark:bg-zinc-950`}
    >
      {/* Hover glow effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-trading-green/0 to-transparent group-hover:from-trading-green/5 dark:group-hover:from-trading-neon/5 transition-colors duration-500 rounded-2xl pointer-events-none"></div>
      
      {/* Top Border Accent */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-transparent to-transparent group-hover:from-trading-green group-hover:via-trading-green/50 dark:group-hover:from-trading-neon dark:group-hover:via-trading-neon/50 transition-all duration-500 opacity-0 group-hover:opacity-100"></div>

      <div className="flex flex-col relative z-10">
        <div className="flex items-start justify-between mb-6">
          <div className="p-3 bg-gray-50 dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-gray-800 transition-colors group-hover:border-trading-green/30 dark:group-hover:border-trading-neon/30">
            {getIcon(type, isLarge, image, title)}
          </div>
          <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mt-2 px-2 py-1 rounded bg-gray-100 dark:bg-zinc-900 border border-gray-200 dark:border-gray-800">
            {type}
          </span>
        </div>
        
        <div className="flex flex-col gap-3">
          <h3 className={`${isLarge ? 'text-3xl md:text-4xl' : 'text-2xl'} font-display font-bold text-gray-900 dark:text-white tracking-tight leading-snug group-hover:text-trading-green dark:group-hover:text-trading-neon transition-colors`}>
            {title}
          </h3>
          <div className="relative">
            <div
              style={shouldClamp ? { maxHeight } : undefined}
              className={`overflow-hidden transition-[max-height] duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] ${
                shouldClamp ? 'will-change-[max-height]' : ''
              }`}
            >
              <p
                ref={descRef}
                className={`text-gray-600 dark:text-gray-400 ${isLarge ? 'text-base md:text-lg' : 'text-sm'} leading-relaxed font-medium whitespace-normal`}
              >
                {description}
              </p>
            </div>

            {/* Subtle fade to imply more content (only when clamped) */}
            {shouldClamp && !isHovered && (
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white dark:from-zinc-950 to-transparent" />
            )}
          </div>
        </div>
        
        <div className="mt-4 overflow-hidden max-h-0 opacity-0 translate-y-2 transition-[max-height,opacity,transform] duration-300 ease-out group-hover:max-h-12 group-hover:opacity-100 group-hover:translate-y-0">
          <div className="flex items-center text-xs font-mono font-bold text-trading-green dark:text-trading-neon uppercase tracking-wider">
            <span className="mr-2">&gt;</span> {getButtonText(type)}
            {link !== '#' && <ExternalLink className="w-3 h-3 ml-2" />}
          </div>
        </div>
      </div>
    </a>
  );
};

export default Card;
